import React, { useState, useEffect } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

interface SystemHeaderProps {
    onSave?: () => void;
    onReset?: () => void;
    openWindows?: string[];
    activeProcesses?: number;
    onCpuClick?: () => void;
    cpuLoad?: number; // Recevoir la charge CPU calculée
    memoryUsage?: number; // Recevoir l'usage mémoire calculé
    notificationCount?: number; // Nombre de notifications non lues
    onNotificationClick?: () => void; // Callback pour ouvrir/fermer les notifications
}

export const SystemHeader: React.FC<SystemHeaderProps> = ({ 
    onSave, 
    onReset, 
    openWindows = [], 
    activeProcesses = 0,
    onCpuClick,
    cpuLoad = 15,
    memoryUsage = 50,
    notificationCount = 0,
    onNotificationClick
}) => {
    const { t } = useLanguage();
    const [time, setTime] = useState(new Date());
    const [currentUser, setCurrentUser] = useState<string>(t('systemHeader.operator'));

    // Récupérer le nom d'utilisateur depuis la sauvegarde
    useEffect(() => {
        const updateUserName = () => {
            const saved = localStorage.getItem('ALBION_SAVE_DATA');
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    if (data.user && data.user.u) {
                        setCurrentUser(data.user.u);
                        return;
                    }
                } catch (e) {}
            }
            
            // Fallback vers l'ancien système
            const legacyUser = localStorage.getItem('albion_user');
            if (legacyUser) {
                try {
                    const user = JSON.parse(legacyUser);
                    if (user.u) {
                        setCurrentUser(user.u);
                        return;
                    }
                } catch (e) {}
            }
            
            // Fallback vers la traduction par défaut
            setCurrentUser(t('systemHeader.operator'));
        };

        // Mettre à jour au chargement
        updateUserName();

        // Écouter les changements de session utilisateur
        const handleUserSessionChange = () => {
            updateUserName();
        };

        window.addEventListener('USER_SESSION_CHANGED', handleUserSessionChange);
        return () => window.removeEventListener('USER_SESSION_CHANGED', handleUserSessionChange);
    }, [t]);

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Couleur de la charge CPU selon le niveau
    const getCpuColor = (load: number) => {
        if (load < 30) return 'text-green-400';
        if (load < 60) return 'text-yellow-400';
        if (load < 80) return 'text-orange-400';
        return 'text-red-400';
    };

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full h-9 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 border-b border-slate-600 flex items-center justify-between px-4 text-xs font-mono text-slate-300 uppercase tracking-widest select-none z-50 shadow-lg shadow-black/20 flex-shrink-0">
            <div className="flex gap-6">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.6)]"></span>
                    <span className="text-cyan-400 font-bold">{t('systemHeader.systemOnline')}</span>
                </div>
                <span className="text-slate-400">{t('systemHeader.user')}: <span className="text-slate-100">{currentUser}</span></span>
                <span className="text-slate-400 hidden sm:inline">{t('systemHeader.encryption')}: <span className="text-emerald-400">{t('systemHeader.encryptionStatus')}</span></span>
            </div>

            <div className="flex gap-6 items-center">
                <span className="text-slate-400 hidden sm:inline">{t('systemHeader.memoryUsage')}: <span className="text-slate-100">{memoryUsage}MB</span></span>
                <button 
                    className="text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCpuClick?.();
                    }}
                    title={t('systemHeader.clickToViewProcesses')}
                >
                    {t('systemHeader.cpuLoad')}: <span className={getCpuColor(cpuLoad)}>{cpuLoad}%</span>
                </button>
                
                {/* Bouton de notifications */}
                <button
                    className="relative flex items-center gap-1 text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        onNotificationClick?.();
                    }}
                    title={notificationCount > 0 ? `${notificationCount} notification(s) en attente` : 'Aucune notification'}
                >
                    {notificationCount > 0 ? (
                        <BellRing size={14} className="animate-pulse" />
                    ) : (
                        <Bell size={14} />
                    )}
                    
                    {/* Badge de notification */}
                    {notificationCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center animate-pulse border border-slate-800">
                            {notificationCount > 9 ? '9+' : notificationCount}
                        </div>
                    )}
                </button>
                
                <span className="text-slate-100 font-bold">14 OCT 1996</span>
            </div>
        </div>
    );
};
